<template>
  <div class="dataset-table-container">
    <table>
      <thead>
        <tr>
          <th
            ref="stickyColOffset0"
            :key="`0-${columnOffsets.join('-')}`"
            class="sticked-left"
          >
            <div class="cell-util">
              <input
                type="checkbox"
                :checked="allRowsSelected"
                @click.stop="toggleSelectAllRows"
              />
            </div>
          </th>
          <th
            ref="stickyColOffset1"
            class="sticked-left"
            :style="{
              left: `${columnOffsets[0]}px`,
            }"
          ></th>
          <th
            ref="stickyColOffset2"
            class="sticked-left"
            :style="{
              left: `${columnOffsets[1]}px`,
            }"
          ></th>
          <th
            ref="stickyColOffset3"
            class="sticked-left"
            :style="{
              left: `${columnOffsets[2]}px`,
            }"
          >
            <div class="cell-content">
              Dataset
              <div class="header-btn-group">
                <ModeSwitch
                  :model-value="sortBy.key === 'name' ? sortBy.order : 'asc'"
                  :options="nameSortOptions"
                  force-select
                  @update:model-value="
                    (val) => {
                      sortBy.key = 'name'
                      sortBy.order = val
                    }
                  "
                >
                  <template #trigger>
                    <IconButton
                      v-tooltip="
                        sortBy.key === 'name'
                          ? sortBy.order === 'asc'
                            ? 'Dataset name — Ascending'
                            : 'Dataset name — Descending'
                          : 'Sort by dataset name'
                      "
                      :icon="
                        sortBy.key === 'name' && sortBy.order === 'desc'
                          ? 'fa-solid fa-sort-alpha-down'
                          : 'fa-solid fa-sort-alpha-up'
                      "
                      size="small"
                      color="primary"
                      :colorful="true"
                      :show-arrow="false"
                    />
                  </template>
                </ModeSwitch>
              </div>
            </div>
          </th>
          <th
            class="sticked-left"
            style="padding: 5px"
            :style="{
              left: `${columnOffsets[3]}px`,
            }"
          ></th>
          <th>
            <div class="cell-content">
              Collection
              <div class="header-btn-group">
                <ModeSwitch
                  :model-value="
                    sortBy.key === 'collection' ? sortBy.order : 'asc'
                  "
                  :options="collectionSortOptions"
                  force-select
                  @update:model-value="
                    (val) => {
                      sortBy.key = 'collection'
                      sortBy.order = val
                    }
                  "
                >
                  <template #trigger>
                    <IconButton
                      v-tooltip="
                        sortBy.key === 'collection'
                          ? sortBy.order === 'asc'
                            ? 'Collection name — Ascending'
                            : 'Collection name — Descending'
                          : 'Sort by collection name'
                      "
                      :icon="
                        sortBy.key === 'collection' && sortBy.order === 'desc'
                          ? 'fa-solid fa-sort-alpha-down'
                          : 'fa-solid fa-sort-alpha-up'
                      "
                      size="small"
                      color="primary"
                      :colorful="true"
                      :show-arrow="false"
                    />
                  </template>
                </ModeSwitch>
              </div>
            </div>
          </th>
          <th>
            <div class="cell-content">
              <slot name="sample-selector">
                <span
                  v-tooltip="'This field is mandatory'"
                  class="icon has-text-danger"
                >
                  <i class="fa-solid fa-asterisk fa-xs" />
                </span>
                <span>{{ metaSampleLabel || 'Sample' }}</span>
              </slot>
            </div>
            <div class="is-flex is-align-items-center" style="gap: 0.25rem">
              <ModeSwitch
                v-tooltip="
                  `Changing the mode applies automatically to rows that are not yet validated (currently: ${unconfiguredRowsCount}). To override rows, use the force-apply buttons on the right, targeting the current selection or all rows.`
                "
                :model-value="defaultSampleMode"
                :options="sampleModeOptions"
                :force-select="true"
                @update:model-value="
                  (m) => {
                    defaultSampleMode = m
                    applyModeToContext(m)
                    if (m === 'existing' && sampleOptions.length === 0)
                      toggleSampleModal()
                  }
                "
              />
              <IconButton
                v-tooltip="
                  selectedRowsIndices.length > 0
                    ? 'Force apply mode to selected rows'
                    : 'No rows selected'
                "
                icon="fa-solid fa-tasks"
                size="small"
                color="primary"
                :colorful="selectedRowsIndices.length > 0"
                :disabled="selectedRowsIndices.length === 0"
                :show-arrow="false"
                @click="setAllSampleMode(defaultSampleMode, selectedDatasets)"
              />
              <IconButton
                v-tooltip="'Force apply mode to all rows'"
                icon="fa-solid fa-list-ul"
                size="small"
                color="primary"
                :colorful="true"
                :show-arrow="false"
                @click="setAllSampleMode(defaultSampleMode, sortedDatasets)"
              />
            </div>
          </th>
          <th
            v-for="{ label } in sampleIdentityFields.map(
              (f) => sampleIdentityFieldsMeta[f],
            )"
            :key="`headlabel${label}`"
          >
            <div class="cell-content">
              <span
                v-tooltip="'This field is mandatory'"
                class="icon has-text-danger"
              >
                <i class="fa-solid fa-asterisk fa-xs" />
              </span>
              <span>{{ label }}</span>
            </div>
          </th>
          <th :style="{ 'min-width': showFilenames ? '700px' : 0 }">
            <div class="cell-content">
              Files
              <div class="header-btn-group">
                <IconButton
                  v-tooltip="
                    showFilenames ? 'Hide filenames' : 'Show filenames'
                  "
                  :icon="
                    showFilenames
                      ? 'fa-solid fa-toggle-on'
                      : 'fa-solid fa-toggle-off'
                  "
                  color="primary"
                  :colorful="true"
                  size="small"
                  @click="showFilenames = !showFilenames"
                />
              </div>
            </div>
          </th>
          <th>
            <div class="cell-content">
              <span
                v-tooltip="'This field is mandatory'"
                class="icon is-small has-text-danger"
              >
                <i class="fa-solid fa-asterisk fa-sm" />
              </span>
              Study
              <div class="header-btn-group">
                <IconButton
                  v-tooltip="'Search existing studies'"
                  icon="fa-solid fa-microscope"
                  color="primary"
                  :colorful="true"
                  size="small"
                  @click="
                    () => {
                      cellIndex = -1
                      openStudyPicker('search')
                    }
                  "
                />
              </div>
            </div>
          </th>
          <th>
            <div class="cell-content">
              <span
                v-tooltip="'This field is mandatory'"
                class="icon is-small has-text-danger"
              >
                <i class="fa-solid fa-asterisk fa-sm" />
              </span>
              Owner
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(dataset, sortedIndex) in sortedDatasets"
          :key="`row-${sortedIndex}`"
          :class="{ 'is-selected': isSelectedRow(sortedIndex) }"
        >
          <td class="has-text-centered sticked-left">
            <div class="cell-util">
              <input
                type="checkbox"
                :checked="isSelectedRow(sortedIndex)"
                @click="toggleSelectRow(sortedIndex, $event)"
              />
            </div>
          </td>
          <td
            class="has-text-centered sticked-left"
            :style="{ left: `${columnOffsets[0]}px` }"
          >
            <div class="cell-util">
              <b>{{ sortedIndex + 1 }}</b>
            </div>
          </td>
          <td
            class="has-text-centered sticked-left"
            :style="{ left: `${columnOffsets[1]}px` }"
          >
            <div class="cell-util">
              <span
                v-if="hasErrorsByRow[sortedIndex]"
                class="icon has-text-danger"
              >
                <i class="fa-solid fa-times" />
              </span>
              <span v-else class="icon has-text-success">
                <i class="fa-solid fa-check" />
              </span>
            </div>
          </td>
          <td
            class="valign-middle sticked-left"
            style="min-width: 220px; max-width: 260px"
            :style="{
              left: `${columnOffsets[2]}px`,
            }"
          >
            <div class="cell-name">
              <span
                v-tooltip="dataset.name"
                class="name-ellipsis mr-2"
                :class="{
                  'has-text-danger has-text-weight-bold':
                    hasPooledSampleByRow[sortedIndex] && !allowPooledSamples,
                }"
              >
                {{ dataset.name }}
              </span>
              <!-- Samples are duplicated within the same collection -->
              <Popover
                v-if="hasPooledSampleByRow[sortedIndex]"
                :triggers="['hover']"
                placement="top"
              >
                <span
                  v-if="!allowPooledSamples"
                  class="icon hoverable-icon has-text-warning"
                >
                  <i class="fa-solid fa-exclamation-triangle" />
                </span>
                <span v-else class="icon has-text-info">
                  <i class="fa-solid fa-info-circle" />
                </span>
                <template #popper>
                  <p
                    class="has-text-weight-bold has-text-info"
                    :class="{ 'has-text-danger': !allowPooledSamples }"
                  >
                    <span class="icon hoverable-icon">
                      <i class="fa-solid fa-exclamation-triangle" />
                    </span>
                    This sample is
                    <u>duplicated within the dataset collection</u>
                  </p>
                  <span>
                    <span class="icon"></span>
                    Duplicated on
                    <span
                      v-if="
                        intraCollectionDuplicates[sortedIndex].length ===
                        sortedDatasets.length
                      "
                    >
                      <b>all</b>
                      rows
                    </span>
                    <span v-else>
                      rows:
                      <b>
                        {{
                          intraCollectionDuplicates[sortedIndex]
                            .map((i) => i + 1)
                            .join(', ')
                        }}
                      </b>
                    </span>
                  </span>
                  <template v-if="!allowPooledSamples">
                    <hr />
                    <span class="help">
                      To allow samples to be re-used within the same collection,
                      please enable
                      <b><u>Allow Sample Reuse</u></b>
                      above the table
                    </span>
                  </template>
                </template>
              </Popover>
              <!-- Samples are duplicated across collection -->
              <Popover
                v-if="
                  interCollectionDuplicates[sortedIndex] &&
                  interCollectionDuplicates[sortedIndex].collections.length > 1
                "
                :triggers="['hover']"
                placement="top"
              >
                <span class="icon hoverable-icon has-text-info">
                  <i class="fa-solid fa-info-circle" />
                </span>
                <template #popper>
                  <p class="has-text-weight-bold has-text-info">
                    <span class="icon hoverable-icon">
                      <i class="fa-solid fa-info-circle" />
                    </span>
                    This sample is
                    <u>duplicated accross dataset collections</u>
                  </p>
                  <span>
                    <span class="icon"></span>
                    Duplicated
                    <span class="pr-1">
                      {{
                        interCollectionDuplicates[sortedIndex].indices.length
                      }}
                    </span>
                    <span>
                      {{
                        pluralize(
                          'time',
                          interCollectionDuplicates[sortedIndex].indices.length,
                        )
                      }}
                    </span>
                    accross collections (in
                    <b>
                      {{
                        interCollectionDuplicates[sortedIndex].collections.join(
                          ', ',
                        )
                      }}
                    </b>
                    ) on rows:
                    <b>
                      {{
                        interCollectionDuplicates[sortedIndex].indices
                          .map((i) => i + 1)
                          .join(', ')
                      }}
                    </b>
                  </span>
                </template>
              </Popover>
            </div>
          </td>
          <td
            :key="`c-color-${
              dataset.isNewSample ? 'new' : `${brandShort}`
            }-${sortedIndex}`"
            class="sticked-left p-1"
            :style="{
              left: `${columnOffsets[3]}px`,
              ...rowStylesPerCollectionName[dataset.collection],
            }"
          ></td>
          <td
            class="valign-middle"
            style="max-width: 220px; text-align: center"
          >
            <div
              v-tooltip="`${dataset.collectionName}`"
              class="tag name-ellipsis"
              :style="rowStylesPerCollectionName[dataset.collection]"
              style="width: 100%; font-weight: 900"
            >
              <span>{{ dataset.collectionName }}</span>
            </div>
          </td>
          <td class="valign-middle" style="min-width: 350px">
            <div class="cell-content cell-sample-inline">
              <!-- Compact trigger: tag for "new", db icon for "existing" -->
              <ModeSwitch
                :model-value="dataset.isNewSample ? 'new' : 'existing'"
                :options="sampleModeOptions"
                :force-select="true"
                @update:model-value="
                  (mode) => {
                    if (mode === 'new') {
                      setNewSample(dataset)
                      transferSampleIdentity(
                        dataset,
                        lastNewSampleIdentity[sortedIndex],
                      )
                    } else {
                      cellIndex = sortedIndex
                      if (sampleOptions.length === 0) {
                        toggleSampleModal()
                      }
                      setExistingSample(
                        dataset,
                        lastExistingSampleIds[sortedIndex],
                      )
                    }
                  }
                "
              >
                <template #trigger>
                  <span
                    v-tooltip="'Switch: Create new / Select existing'"
                    class="sample-mode-trigger"
                  >
                    <span
                      v-if="dataset.isNewSample !== false"
                      class="sample-mode-new-label"
                    >
                      NEW
                    </span>
                    <i v-else class="fa-solid fa-database" />
                    <i class="fa-solid fa-angle-down sample-mode-caret" />
                  </span>
                </template>
              </ModeSwitch>

              <!-- Input area -->
              <div class="sample-input-grow">
                <div v-if="dataset.isNewSample === false" class="field">
                  <SelectContainer
                    :field-id="`sample-${sortedIndex}`"
                    :value="dataset.sample"
                    :options="sampleOptions"
                    placeholder="Chose existing sample"
                    track-by="_flatId"
                    label="_flatName"
                    :validation-rules="[
                      {
                        error_message: 'One sample should be selected',
                        name: 'required',
                      },
                    ]"
                    validation-scope="sampleAssigner"
                    @input="
                      (sampleOpt) => {
                        cellIndex = sortedIndex
                        handleSelectSampleOption(dataset, sampleOpt)
                      }
                    "
                  />
                </div>
                <div v-else-if="dataset.isNewSample === true" class="field">
                  <StringDetailEdit
                    :field-id="`name-${sortedIndex}`"
                    :value="dataset.newSampleName"
                    placeholder="New sample name"
                    :validation-rules="[
                      { name: 'required', error_message: 'Name is required' },
                      { name: 'maxLength', arguments: [255] },
                    ]"
                    validation-scope="sampleAssigner"
                    @input="(name) => (dataset.newSampleName = name)"
                  />
                </div>
              </div>

              <!-- Ellipsis menu -->
              <div class="ellipsis-menu">
                <IconButton
                  icon="fa-solid fa-ellipsis-v"
                  :show-arrow="false"
                  align="end"
                  size="small"
                >
                  <template #dropdown>
                    <a
                      tabindex="0"
                      class="dd-item"
                      :class="{
                        disabled:
                          selectedDatasets.length === 0 ||
                          hasEmptyOrNullSampleByRow[sortedIndex],
                      }"
                      @click="handleCloneSampleCell(dataset, selectedDatasets)"
                    >
                      <span style="margin-right: auto">
                        Propagate this sample to selected rows
                      </span>
                      <span class="icon is-small ml-2">
                        <i class="fa-solid fa-tasks" aria-hidden="true" />
                      </span>
                    </a>
                    <a
                      tabindex="0"
                      class="dd-item"
                      :class="{
                        disabled: hasEmptyOrNullSampleByRow[sortedIndex],
                      }"
                      @click="handleCloneSampleCell(dataset, sortedDatasets)"
                    >
                      <span style="margin-right: auto">
                        Propagate this sample to all rows
                      </span>
                      <span class="icon is-small ml-2">
                        <i class="fa-solid fa-list-ul" aria-hidden="true" />
                      </span>
                    </a>
                  </template>
                </IconButton>
              </div>
            </div>
          </td>
          <td
            v-for="fieldName in sampleIdentityFields"
            :key="`identityfield-${fieldName}`"
            class="valign-middle"
            style="min-width: 200px"
          >
            <div
              v-if="sampleIdentityFieldsMeta[fieldName]"
              class="cell-content"
            >
              <div>
                <FieldComponent
                  :field-id="`${fieldName}-${sortedIndex}`"
                  :field-meta="sampleIdentityFieldsMeta[fieldName]"
                  :is-edit-mode="dataset.isNewSample === true"
                  :data="dataset[fieldName] || { value: null }"
                  :max-height="200"
                  validation-scope="sampleAssigner"
                  @edit="(value) => (dataset[fieldName] = { value })"
                />
              </div>
              <div v-if="dataset.isNewSample === true" class="ellipsis-menu">
                <IconButton
                  icon="fa-solid fa-ellipsis-v"
                  :show-arrow="false"
                  align="end"
                  size="small"
                >
                  <template #dropdown>
                    <a
                      tabindex="0"
                      class="dd-item"
                      :class="{
                        disabled:
                          !dataset[fieldName] || selectedDatasets.length === 0,
                      }"
                      @click="
                        handleCloneCell(
                          dataset,
                          fieldName,
                          newSampleSelectedDatasets,
                        )
                      "
                    >
                      <span style="margin-right: auto">
                        Propagate this
                        {{ sampleIdentityFieldsMeta[fieldName].label }}
                        to selected rows
                      </span>
                      <span class="icon is-small ml-2">
                        <i class="fa-solid fa-tasks" aria-hidden="true" />
                      </span>
                    </a>
                    <a
                      tabindex="0"
                      class="dd-item"
                      :class="{ disabled: !dataset[fieldName] }"
                      @click="
                        handleCloneCell(
                          dataset,
                          fieldName,
                          newSampleSortedDatasets,
                        )
                      "
                    >
                      <span style="margin-right: auto">
                        Propagate this
                        {{ sampleIdentityFieldsMeta[fieldName].label }}
                        to all rows
                      </span>
                      <span class="icon is-small ml-2">
                        <i class="fa-solid fa-list-ul" aria-hidden="true" />
                      </span>
                    </a>
                  </template>
                </IconButton>
              </div>
            </div>
          </td>
          <td class="valign-middle">
            <div v-if="showFilenames" style="display: block">
              <div
                v-for="(file, fileIdx) in dataset.datafiles"
                :key="`file${fileIdx}-${file.id}`"
                class="is-flex pb-1"
              >
                <div class="is-flex-grow-0">
                  <b class="pr-2">{{ fileIdx + 1 }}</b>
                </div>
                <div
                  v-tooltip="`${file.uri || file.name}`"
                  style="word-break: break-all"
                >
                  <code class="has-text-primary">{{ file.uri }}</code>
                </div>
              </div>
            </div>
            <div
              v-else
              v-tooltip="'Show filenames'"
              style="width: 100%; text-align: center; cursor: pointer"
              @click="showFilenames = !showFilenames"
            >
              …
            </div>
          </td>

          <td
            class="valign-middle"
            :class="{
              'highlight-danger': studyOptions.length < 1,
              first: sortedIndex === 0,
              last: sortedIndex === sortedDatasets.length - 1,
            }"
          >
            <div class="cell-content">
              <div>
                <div v-if="studyOptions.length > 0">
                  <SelectContainer
                    :field-id="`studies-${sortedIndex}`"
                    style="min-width: 300px"
                    :value="dataset.studies || null"
                    :options="studyOptions"
                    placeholder="Chose study"
                    track-by="_flatId"
                    label="_displayName"
                    :multiple="true"
                    :validation-rules="[
                      {
                        name: 'required',
                        arguments: [],
                        error_message: 'Please select a study',
                      },
                    ]"
                    validation-scope="sampleAssigner"
                    @input="(s) => (dataset.studies = s)"
                  />
                </div>
                <div v-else class="is-flex is-align-items-flex-start">
                  <BaseButton
                    :icon="['fa-solid', 'fa-search']"
                    variant="primary"
                    :outlined="true"
                    size="small"
                    label="Load"
                    class="m-2"
                    style="width: 100%"
                    @click="
                      () => {
                        cellIndex = sortedIndex
                        openStudyPicker('search')
                      }
                    "
                  />
                  <BaseButton
                    :icon="['fa-solid', 'fa-plus-circle']"
                    variant="primary"
                    :outlined="true"
                    size="small"
                    label="Create"
                    class="m-2"
                    style="width: 100%"
                    @click="
                      () => {
                        cellIndex = sortedIndex
                        openStudyPicker('create')
                      }
                    "
                  />
                </div>
              </div>
              <div class="ellipsis-menu">
                <IconButton
                  icon="fa-solid fa-ellipsis-v"
                  :show-arrow="false"
                  align="end"
                  size="small"
                >
                  <template #dropdown>
                    <a
                      tabindex="0"
                      class="dd-item"
                      :class="{
                        disabled:
                          !dataset.studies || selectedDatasets.length === 0,
                      }"
                      @click="
                        handleCloneCell(dataset, 'studies', selectedDatasets)
                      "
                    >
                      <span style="margin-right: auto">
                        Propagate this study to selected rows
                      </span>
                      <span class="icon is-small ml-2">
                        <i class="fa-solid fa-tasks" aria-hidden="true" />
                      </span>
                    </a>
                    <a
                      tabindex="0"
                      class="dd-item"
                      :class="{ disabled: !dataset.studies }"
                      @click="
                        handleCloneCell(dataset, 'studies', sortedDatasets)
                      "
                    >
                      <span style="margin-right: auto">
                        Propagate this study to all rows
                      </span>
                      <span class="icon is-small ml-2">
                        <i class="fa-solid fa-list-ul" aria-hidden="true" />
                      </span>
                    </a>
                    <hr class="dd-divider" />
                    <a
                      tabindex="0"
                      class="dd-item"
                      @click="
                        () => {
                          cellIndex = sortedIndex
                          openStudyPicker('search')
                        }
                      "
                    >
                      <span style="margin-right: auto">
                        Load existing studies
                      </span>
                      <span class="icon is-small ml-2">
                        <i class="fa-solid fa-search" aria-hidden="true" />
                      </span>
                    </a>
                    <a
                      tabindex="0"
                      class="dd-item"
                      @click="
                        () => {
                          cellIndex = sortedIndex
                          openStudyPicker('create')
                        }
                      "
                    >
                      <span style="margin-right: auto">Create a new study</span>
                      <span class="icon is-small ml-2">
                        <i class="fa-solid fa-plus-circle" aria-hidden="true" />
                      </span>
                    </a>
                  </template>
                </IconButton>
              </div>
            </div>
          </td>
          <td class="valign-middle" style="width: 250px">
            <div class="cell-content">
              <div class="field">
                <SelectContainer
                  :field-id="`owner-${sortedIndex}`"
                  :value="dataset.owner || null"
                  :options="dropdownOptions['owner']"
                  placeholder="Chose owner"
                  track-by="id"
                  label="username"
                  :validation-rules="[
                    {
                      name: 'required',
                      arguments: [],
                      error_message: 'Please select an owner',
                    },
                  ]"
                  :absolute-pos-dropdown="false"
                  validation-scope="sampleAssigner"
                  @input="(o) => (dataset.owner = o)"
                />
              </div>
              <div class="ellipsis-menu">
                <IconButton
                  icon="fa-solid fa-ellipsis-v"
                  :show-arrow="false"
                  align="end"
                  size="small"
                >
                  <template #dropdown>
                    <a
                      tabindex="0"
                      class="dd-item"
                      :class="{
                        disabled:
                          !dataset.owner || selectedDatasets.length === 0,
                      }"
                      @click="
                        handleCloneCell(dataset, 'owner', selectedDatasets)
                      "
                    >
                      <span style="margin-right: auto">
                        Propagate this owner to selected rows
                      </span>
                      <span class="icon is-small ml-2">
                        <i class="fa-solid fa-tasks" aria-hidden="true" />
                      </span>
                    </a>
                    <a
                      tabindex="0"
                      class="dd-item"
                      :class="{ disabled: !dataset.owner }"
                      @click="handleCloneCell(dataset, 'owner', sortedDatasets)"
                    >
                      <span style="margin-right: auto">
                        Propagate this owner to all rows
                      </span>
                      <span class="icon is-small ml-2">
                        <i class="fa-solid fa-list-ul" aria-hidden="true" />
                      </span>
                    </a>
                  </template>
                </IconButton>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import chroma from 'chroma-js'
import { cloneDeep, isEqual, some } from 'lodash-es'
import pluralize from 'pluralize'
import { computed, defineAsyncComponent, ref, watchEffect } from 'vue'

import SelectContainer from '@/components/SelectContainer.vue'
const StringDetailEdit = defineAsyncComponent(
  () => import('@/components/fields/type/string/detail/edit/index.vue'),
)

import { brandShort } from '@/../app.config'
import { useSelectableRecords } from '@/composables/useSelectableRecords'
import { alphaNumSort, uuidv4 } from '@/utils'
import { getForegroundColor } from '@/utils/color'

const DEFAULT_SORT_BY = { key: 'name', order: 'asc' }
const makeSortOptions = (prefix) => [
  { value: 'asc', label: `${prefix} — Ascending` },
  { value: 'desc', label: `${prefix} — Descending` },
]
const NAME_SORT_OPTIONS = makeSortOptions('Dataset name')
const COLLECTION_SORT_OPTIONS = makeSortOptions('Collection name')
const SAMPLE_MODE_OPTIONS = [
  { value: 'new', label: 'Create new' },
  { value: 'existing', label: 'Select existing', icon: 'fa-solid fa-database' },
]

export default {
  name: 'SampleAssigner',
  components: {
    SelectContainer,
    StringDetailEdit,
  },
  emits: [
    'error',
    'update-samples',
    'update-datasets',
    'update-has-intracollection-duplication',
  ],
  props: {
    datasetsData: {
      type: Array,
      default: () => [],
    },
    metaSample: {
      type: Object,
      default: null,
    },
    sampleIdentityFields: {
      type: Array,
      default: () => [],
    },
    sampleOptions: {
      type: Array,
      default: () => [],
    },
    sampleGroups: {
      type: Object,
      default: () => {},
    },
    studyOptions: {
      type: Array,
      default: () => [],
    },
    dropdownOptions: {
      type: Object,
      default: () => {},
    },
    allowPooledSamples: {
      type: Boolean,
      default: false,
    },
    toggleSampleModal: {
      type: Function,
      default: () => {},
    },
    openStudyPicker: {
      type: Function,
      default: () => {},
    },
  },
  setup(props) {
    const sampleModelAttrs = computed(
      () => props.metaSample?.model?.attributes || [],
    )
    const sampleViewAttrs = computed(
      () => props.metaSample?.view?.detail?.attributes || [],
    )
    const sampleNameModel = computed(() =>
      sampleModelAttrs.value.find((x) => x.name === 'name'),
    )
    const sampleNameView = computed(() =>
      sampleViewAttrs.value.find((x) => x.name === 'name'),
    )

    // Merge model + view attrs so FieldResolver receives widget metadata
    // (e.g. widget.edit.type = 'Barcode') that only lives in view attrs.
    const sampleIdentityFieldsMeta = computed(() => {
      const viewByName = sampleViewAttrs.value.reduce((acc, attr) => {
        acc[attr.name] = attr
        return acc
      }, {})
      return (
        sampleModelAttrs.value?.reduce((fieldsMeta, attr) => {
          if (props.sampleIdentityFields.includes(attr.name)) {
            fieldsMeta[attr.name] = {
              ...attr,
              ...(viewByName[attr.name] || {}),
            }
          }
          return fieldsMeta
        }, {}) || {}
      )
    })

    const datasets = ref(
      props.datasetsData.map((dataset) => {
        return {
          ...dataset,
          isNewSample: true,
          newSampleName: dataset.name,
          sample: null,
          ...props.sampleIdentityFields.reduce((idFields, fieldKey) => {
            const fieldModel = sampleIdentityFieldsMeta.value[fieldKey]
            const defaultValue = fieldModel.default
            return {
              ...idFields,
              [fieldKey]: dataset[fieldKey] || { value: defaultValue },
            }
          }, {}),
        }
      }),
    )
    const sortBy = ref(DEFAULT_SORT_BY)
    const sortedDatasets = computed(() => {
      return datasets.value.slice().sort((a, b) => {
        const collComp =
          (a.collection &&
            b.collection &&
            alphaNumSort(a.collection, b.collection)) ||
          (a.collection ? 1 : b.collection ? -1 : 0)
        const nameComp = alphaNumSort(a.name, b.name)
        let comp = 0
        if (sortBy.value?.key === 'name') {
          if (nameComp !== 0) {
            comp = sortBy.value.order === 'asc' ? -nameComp : nameComp
          } else {
            comp = -collComp
          }
        } else if (sortBy.value?.key === 'collection') {
          if (collComp !== 0) {
            comp = sortBy.value.order === 'asc' ? -collComp : collComp
          } else {
            comp = -nameComp
          }
        }
        return comp
      })
    })

    const datasetsMandatoryKeys = computed(() => {
      return [...props.sampleIdentityFields, 'studies', 'owner']
    })

    const v$ = useVuelidate({}, {}, { $scope: 'sampleAssigner' })
    let validationErrorsByRow = ref([])
    watchEffect(() => {
      const errors = []
      sortedDatasets.value.forEach((d, dIndex) => {
        const datasetErrors = {}
        const expectedKeys = [
          d.isNewSample ? 'name' : 'sample',
          ...datasetsMandatoryKeys.value,
        ]
        for (let k of expectedKeys) {
          const v$key = `${k}-${dIndex}`
          if (v$key in v$.value && v$.value[v$key].$errors.length > 0)
            datasetErrors[k] = v$.value[v$key]
        }
        errors.push(Object.keys(datasetErrors).length ? datasetErrors : null)
      })
      validationErrorsByRow.value = errors
    })

    const {
      selectedIndices: selectedRowsIndices,
      allSelected: allRowsSelected,
      toggleSelect: toggleSelectRow,
      toggleSelectAll: toggleSelectAllRows,
      isSelected: isSelectedRow,
    } = useSelectableRecords(sortedDatasets)

    const baseColors = chroma.scale('Pastel1').colors(9)
    const newSamplesUuids = props.datasetsData.map(() => uuidv4())
    return {
      sampleNameModel,
      sampleNameView,
      baseColors,
      newSamplesUuids,
      v$,
      sortBy,
      sampleIdentityFieldsMeta,
      sortedDatasets,
      validationErrorsByRow,
      selectedRowsIndices,
      allRowsSelected,
      toggleSelectRow,
      toggleSelectAllRows,
      isSelectedRow,
    }
  },
  data() {
    return {
      brandShort,
      nameSortOptions: NAME_SORT_OPTIONS,
      collectionSortOptions: COLLECTION_SORT_OPTIONS,
      sampleModeOptions: SAMPLE_MODE_OPTIONS,
      cloneCount: 0,
      cellIndex: -1,
      defaultSampleMode: 'new',
      showFilenames: false,
      lastExistingSampleIds: [],
      lastNewSampleIdentity: [],
      columnOffsets: [],
      columnResizeObserver: null,
    }
  },
  computed: {
    metaSampleLabel() {
      return ''
    },
    selectedDatasets() {
      return this.selectedRowsIndices.map((i) => this.sortedDatasets[i])
    },
    newSampleSortedDatasets() {
      return this.sortedDatasets.filter(
        (d, i) => !this.isGroupRow(i) && d.isNewSample === true,
      )
    },
    newSampleSelectedDatasets() {
      return this.selectedRowsIndices
        .filter(
          (i) =>
            !this.isGroupRow(i) && this.sortedDatasets[i].isNewSample === true,
        )
        .map((i) => this.sortedDatasets[i])
    },
    hasEmptyOrNullSampleByRow() {
      return this.sortedDatasets.map((d) => this.isSampleEmpty(d))
    },
    unconfiguredRowsCount() {
      return this.sortedDatasets.filter((d) => this.isSampleUnconfigured(d))
        .length
    },
    sampleIdentities() {
      return this.sortedDatasets.map((dataset) => {
        const id = dataset?.sample?._flatId
        const name = dataset.newSampleName
        const baseIdentity = dataset.isNewSample
          ? { name: { value: { name } } }
          : { id }
        return {
          // id or name
          ...baseIdentity,
          // other identity fields
          // could be omitted for sample with ID (i.e. an existing sample )
          ...this.sampleIdentityFields.reduce(
            (sampleIdentityFieldsDict, idKey) => {
              const fieldModel = this.sampleIdentityFieldsMeta[idKey]
              const defaultValue = fieldModel.default
              return {
                ...sampleIdentityFieldsDict,
                [idKey]: dataset[idKey] || defaultValue,
              }
            },
            {},
          ),
        }
      })
    },
    duplicatedSamples() {
      return this.sampleIdentities.map((identity) =>
        this.identifySampleDuplicates(identity, this.sampleIdentities),
      )
    },
    collectionSampleIdentities() {
      return this.sampleIdentities.map((sample, index) => {
        return {
          collection: this.sortedDatasets[index].collection || null,
          ...sample,
        }
      })
    },
    collections() {
      const collections = {}
      this.sortedDatasets.forEach((d) => {
        const id = d.collection
        const name = d.collectionName
        collections[id] = { name }
      })
      return collections
    },
    intraCollectionDuplicates() {
      const intraCollectionDuplicates = []
      for (
        let indexRef = 0;
        indexRef < this.sampleIdentities.length;
        indexRef++
      ) {
        const collectionSampleRef = this.collectionSampleIdentities[indexRef]
        const refDuplicatedSamples = this.duplicatedSamples[indexRef]
        let dups = refDuplicatedSamples?.reduce(
          (dupIndices, isDup, dupIndex) => {
            const dup = this.collectionSampleIdentities[dupIndex]
            const isRefCollecNull = collectionSampleRef.collection === null
            const isDupCollecNull = dup.collection === null
            const bothCollecNull = isRefCollecNull && isDupCollecNull
            const bothSameCollect =
              collectionSampleRef.collection === dup.collection
            if (isDup && (bothCollecNull || bothSameCollect)) {
              dupIndices = dupIndices.concat(dupIndex)
            }
            return dupIndices
          },
          [],
        )
        // exactly one dup is itself, ignore
        dups = !dups || dups?.length === 1 ? [] : dups
        intraCollectionDuplicates.push(dups)
      }
      return intraCollectionDuplicates
    },
    interCollectionDuplicates() {
      const interCollectionDuplicates = []
      for (
        let indexRef = 0;
        indexRef < this.sampleIdentities.length;
        indexRef++
      ) {
        const interDupIndices = []
        const interDupCollections = new Set()
        const refDuplicatedSamples = this.duplicatedSamples[indexRef]
        refDuplicatedSamples?.forEach((isDup, dupIndex) => {
          const dup = this.collectionSampleIdentities[dupIndex]
          if (isDup) {
            interDupIndices.push(dupIndex)
            interDupCollections.add(dup.collection)
          }
        })
        let interDup = null
        if (interDupIndices.length > 0) {
          interDupIndices.length
          const sortedDupIndices = interDupIndices.sort((a, b) => a - b)
          const uniqDupCollections = [...interDupCollections]
          const uniqDupCollectionsNames = uniqDupCollections.map(
            (cId) => this.collections[cId]?.name,
          )
          interDup = {
            indices: sortedDupIndices,
            collections: uniqDupCollectionsNames,
          }
        }
        interCollectionDuplicates.push(interDup)
      }
      return interCollectionDuplicates
    },
    hasPooledSampleByRow() {
      return this.intraCollectionDuplicates?.map(
        (_, datasetIndex) =>
          this.intraCollectionDuplicates[datasetIndex]?.length > 0,
      )
    },
    hasPooledSample() {
      return some(this.hasPooledSampleByRow)
    },
    hasErrorsByRow() {
      return this.sortedDatasets.map((_, i) =>
        this.validationErrorsByRow[i] ||
        (!this.allowPooledSamples && this.hasPooledSampleByRow[i])
          ? true
          : false,
      )
    },
    hasSomeError() {
      return some(this.hasErrorsByRow)
    },
    duplicatedIndicesByRow() {
      return this.intraCollectionDuplicates?.map(
        (intraDupIndices, sortedIndex) => {
          const interDupIndices =
            this.interCollectionDuplicates[sortedIndex]?.indices || []
          let indices = [...(intraDupIndices || []), ...interDupIndices]
          return indices
            .filter((c, i) => indices.indexOf(c) === i) // uniq
            .sort((a, b) => a - b)
        },
      )
    },
    uniqueSamples() {
      return this.duplicatedIndicesByRow.reduce(
        (reducedIndices, indices, i) => {
          const sampleOccurencesIndices = indices.length === 0 ? [i] : indices
          const key = sampleOccurencesIndices.join('') || i
          if (sampleOccurencesIndices.length > 0 && !(key in reducedIndices)) {
            const oneDupIndex = sampleOccurencesIndices.slice().pop()
            const dupSampleIdentity = this.sampleIdentities[oneDupIndex]
            let samples = []
            if ('id' in dupSampleIdentity) {
              const isSampleGroup =
                this.sampleGroups && dupSampleIdentity.id in this.sampleGroups
              if (isSampleGroup) {
                const group = this.sampleGroups[dupSampleIdentity.id]
                samples = Object.values(group.samples).map((s) => ({
                  ...s,
                  // new samples will have a _id key - existing samples will
                  // have a s.id.value filled out.
                  new: { value: s._id ? true : false },
                  id: { value: s._id ? s._id : s.id?.value },
                }))
              } else {
                samples = [
                  {
                    id: { value: dupSampleIdentity.id },
                    new: { value: false },
                  },
                ]
              }
            } else {
              samples = [
                {
                  ...dupSampleIdentity,
                  id: { value: this.newSamplesUuids[i] },
                  new: { value: true },
                },
              ]
            }
            reducedIndices[key] = samples
          }
          return reducedIndices
        },
        {},
      )
    },
    datasetSamples() {
      return Object.values(this.uniqueSamples).reduce(
        (sampleFlatArray, samples) => {
          samples = samples.map((s) => {
            // TODO: Here should I de nest everything ? Before, we used to
            // deal with fairly simple object with a few fields (ID fields)
            // now that sample groups are handled, we may have full samples objects...
            return Object.keys(s).reduce((sampleFields, idKey) => {
              let value = s[idKey].value
              if (idKey === 'name') value = s.name.value.name
              if (idKey === 'owned_by')
                value = s[idKey]?.value?.id ?? s[idKey]?.value
              return { ...sampleFields, [idKey]: value }
            }, {})
          })
          return [...sampleFlatArray, ...samples]
        },
        [],
      )
    },
    datasetSamplesData() {
      return this.sortedDatasets.map(
        ({ collection, datafiles, name, owner, studies }, index) => {
          const dupSamplesIndices = this.duplicatedIndicesByRow[index]
          const hasDups = dupSamplesIndices.length > 0
          const dupSamplesId = dupSamplesIndices.join('')
          const uniqueSamplesId = (hasDups && dupSamplesId) || index
          return {
            collection,
            datafiles,
            name,
            owner,
            studies,
            samples: this.uniqueSamples[uniqueSamplesId].map((s) => s.id.value),
          }
        },
      )
    },
    rowStyles() {
      return this.baseColors.map((c) => {
        const fgColor = getForegroundColor(c)
        return {
          backgroundColor: c,
          color: fgColor,
        }
      })
    },
    rowStylesPerCollectionName() {
      const maxStyle = this.rowStyles.length
      return Object.keys(this.collections).reduce(
        (styles, collectionId, collectionIndex) => {
          const constraintCollIndex = Math.floor(collectionIndex / maxStyle)
          const styleIndex =
            constraintCollIndex > 0 ? constraintCollIndex - 1 : collectionIndex
          styles[collectionId] = this.rowStyles[styleIndex]
          return styles
        },
        {},
      )
    },
  },
  methods: {
    pluralize,
    isSampleEmpty(dataset) {
      return (
        (dataset.isNewSample === true &&
          (dataset.newSampleName === null || dataset.newSampleName === '')) ||
        (dataset.isNewSample === false && dataset.sample === null)
      )
    },
    isSampleUnconfigured(dataset) {
      if (dataset.isNewSample === false) return dataset.sample === null
      // new mode: treat as unconfigured if name is still the auto-filled
      // default and no identity field has been explicitly set
      const nameIsDefault =
        !dataset.newSampleName || dataset.newSampleName === dataset.name
      const noIdentityData = this.sampleIdentityFields.every(
        (f) => dataset[f]?.value == null || dataset[f]?.value === '',
      )
      return nameIsDefault && noIdentityData
    },
    isGroupRow(sortedIndex) {
      const identity = this.sampleIdentities[sortedIndex]
      return !!(
        identity?.id &&
        this.sampleGroups &&
        identity.id in this.sampleGroups
      )
    },
    applyModeToDatasets(mode, targets) {
      for (const d of targets) {
        if (mode === 'new') {
          this.setNewSample(d)
          this.transferSampleIdentity(d, null)
        } else {
          this.setExistingSample(d)
        }
      }
    },
    setAllSampleMode(mode, targets) {
      this.defaultSampleMode = mode
      this.applyModeToDatasets(mode, targets ?? this.sortedDatasets)
    },
    applyModeToContext(mode) {
      const hasSelection = this.selectedRowsIndices.length > 0
      const targets = hasSelection
        ? this.selectedRowsIndices.map((i) => this.sortedDatasets[i])
        : this.sortedDatasets.filter((d) => this.isSampleUnconfigured(d))
      this.applyModeToDatasets(mode, targets)
    },
    setNewSample(dataset) {
      dataset.isNewSample = true
      dataset.sample = null
    },
    transferSampleIdentity(dataset, sampleObj) {
      this.sampleIdentityFields.forEach((idField) => {
        const defaultValue = this.sampleIdentityFieldsMeta[idField].default
        dataset[idField] = sampleObj?.[idField] ?? {
          value: defaultValue ?? null,
        }
      })
    },
    identifySampleDuplicates(refSampleIdentity, sampleIdentities) {
      return sampleIdentities.map((compIdentity, sortedIndex) => {
        return (
          (('id' in refSampleIdentity && refSampleIdentity.id !== null) ||
            'name' in refSampleIdentity) &&
          !this.validationErrorsByRow[sortedIndex] &&
          isEqual(refSampleIdentity, compIdentity)
        )
      })
    },
    setExistingSample(dataset, sampleId) {
      dataset.isNewSample = false
      if (sampleId) {
        const sampleOpt =
          this.sampleOptions.find(({ _flatId }) => _flatId === sampleId) || null
        this.handleSelectSampleOption(dataset, sampleOpt)
      } else {
        this.transferSampleIdentity(dataset, null)
      }
    },
    savePreviousSampleIdentity(index, identity) {
      if (identity && 'id' in identity) {
        // only save existing sample when not null
        if (identity.id !== null) {
          this.lastExistingSampleIds[index] = identity.id
        }
      } else {
        // save whole id for new sample, to recover also id fields later
        this.lastNewSampleIdentity[index] = identity
      }
    },
    handleSelectSampleOption(dataset, sampleOpt) {
      dataset.sample = sampleOpt
      this.transferSampleIdentity(dataset, sampleOpt)
    },
    handleCloneCell(from, key, to) {
      const value = cloneDeep(from[key])
      for (let d of to) {
        d[key] = value
      }
      this.cloneCount++
    },
    handleCloneSampleCell(from, to) {
      if (from.isNewSample === true) {
        to.forEach((dataset) => {
          this.setNewSample(dataset)
          this.handleCloneCell(from, 'newSampleName', [dataset])
        })
      } else if (from.isNewSample === false) {
        to.forEach((dataset) => {
          const sampleId = from.sample._flatId
          this.setExistingSample(dataset, sampleId)
        })
      }
    },
    /* Layout & Anim */
    computeColumnOffsets() {
      const refs = [
        this.$refs?.stickyColOffset0,
        this.$refs?.stickyColOffset1,
        this.$refs?.stickyColOffset2,
        this.$refs?.stickyColOffset3,
      ]
      let total = 0
      this.columnOffsets = refs.map((el) => {
        total += el?.offsetWidth || 0
        return total
      })
    },
  },
  mounted() {
    if (this.collections.length > 1) this.sortBy.key = 'collection'
    // Observe frozen header cells — recompute offsets whenever any resizes
    this.$nextTick(() => {
      this.computeColumnOffsets()
      const observer = new ResizeObserver(() => this.computeColumnOffsets())
      const refs = [
        this.$refs.stickyColOffset0,
        this.$refs.stickyColOffset1,
        this.$refs.stickyColOffset2,
        this.$refs.stickyColOffset3,
      ]
      refs.forEach((el) => el && observer.observe(el))
      this.columnResizeObserver = observer
    })
  },
  beforeUnmount() {
    this.columnResizeObserver?.disconnect()
  },
  watch: {
    hasSomeError: {
      handler: function (hasError) {
        this.$emit('error', hasError)
      },
      immediate: true,
    },
    sampleIdentities: {
      handler: function (newSamplesIds, oldSampleIds) {
        newSamplesIds.forEach((_, i) => {
          this.savePreviousSampleIdentity(i, oldSampleIds[i])
        })
      },
      immediate: false,
      deep: true,
    },
    datasetSamples(samples) {
      this.$emit('update-samples', samples)
    },
    datasetSamplesData(datasets) {
      this.$emit('update-datasets', datasets)
    },
    hasPooledSample: {
      handler: function (hasDup) {
        this.$emit('update-has-intracollection-duplication', hasDup)
      },
      immediate: true,
    },
  },
}
</script>

<style lang="scss" scoped>
// ── Table base ──────────────────────────────────────────────────
// --table-row-h: matches Bulma's standard input height (2.5em at 1rem base).
// Used as the reference height for the first content line in every cell so
// trigger, input, badge, ellipsis, and utility icons all share the same axis.
.dataset-table-container {
  position: relative;
  --table-row-h: 2.5rem;
}

.dataset-table-container table {
  border-spacing: 0;
  border-collapse: separate;
  width: 100%;
}

.dataset-table-container table th,
.dataset-table-container table td {
  padding: var(--space-2) var(--space-4);
  // top-align so error messages below inputs extend downward without shifting
  // adjacent cells (vertical-align: middle would re-center on a tall cell).
  vertical-align: top;
}

// ── Row separators (box-shadow, NOT border — borders don't travel
// with sticky-positioned cells) ──────────────────────────────────
.dataset-table-container table td {
  box-shadow: inset 0 -1px 0 var(--border-default);
}

// ── Sticky header row ───────────────────────────────────────────
.dataset-table-container table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: var(--bg-default);
  box-shadow: inset 0 -2px 0 var(--border-default);
}

// ── Frozen columns (sticky left) ────────────────────────────────
.dataset-table-container table td.sticked-left {
  position: sticky;
  z-index: 1;
  background-color: var(--white);
  box-shadow: inset 0 -1px 0 var(--border-default);
}

// Frozen header corner: highest layer (sticky top + left)
.dataset-table-container table th.sticked-left {
  z-index: 3;
}

// Fill sub-pixel rendering gap between status col (3) and dataset name col (4).
// border-right travels with the sticky element; offsetWidth includes it so the
// JS-computed left offset for col 4 stays pixel-perfect. Same pattern as MergeSampleGroup.vue.
.dataset-table-container table td.sticked-left:nth-child(3),
.dataset-table-container table th.sticked-left:nth-child(3) {
  border-right: 1px solid var(--border-default);
  box-sizing: border-box;
}

// Right edge on last frozen column (Dataset = 5th child)
.dataset-table-container table td.sticked-left:nth-child(5) {
  box-shadow:
    inset 0 -1px 0 var(--border-default),
    2px 0 0 var(--border-default);
}

.dataset-table-container table th.sticked-left:nth-child(5) {
  box-shadow:
    inset 0 -2px 0 var(--border-default),
    2px 0 0 var(--border-default);
}

// ── First column anchored at left: 0 ───────────────────────────
.dataset-table-container table th:first-of-type,
.dataset-table-container table td:first-of-type {
  left: 0;
}

// .cell-util: reference-height flex wrapper for inline utility content.
// Vertically centers checkbox / row-number / status icon within --table-row-h
// so they share the same horizontal axis as inputs in adjacent cells.
.cell-util {
  height: var(--table-row-h);
  display: flex;
  align-items: center;
  justify-content: center;
}

// ── Row selection ───────────────────────────────────────────────
.dataset-table-container table tr.is-selected td {
  background-color: var(--border-default);
}

// ── Cell content layout ─────────────────────────────────────────
// Headers: center label + action buttons vertically; gap keeps text from
// abutting icons in narrow columns (e.g. Files when filenames are hidden).
.dataset-table-container table th > div:not(.tag):not(.cell-util) {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--table-row-h);
}

// Data cells: anchor content at top so error rows grow downward.
// min-height ensures short cells still occupy the reference line height.
.dataset-table-container
  table
  td
  > div:not(.tag):not(.cell-sample-inline):not(.cell-util):not(.cell-name) {
  display: flex;
  align-items: flex-start;
  min-height: var(--table-row-h);
}

.cell-name {
  display: flex;
  align-items: center;
  min-height: var(--table-row-h);
}

// Header action buttons: pushed right, vertically centered, compact gap.
.header-btn-group {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

// Strip ModeSwitch chrome when used as a pure icon-button trigger in headers.
.header-btn-group :deep(.mode-switch) {
  margin-bottom: 0;
  border-bottom: none;
  padding: 0;
}

th .cell-content div:first-child,
td .cell-content div:first-child {
  flex-grow: 1;
}

td .cell-content .ellipsis-menu {
  padding: 0 var(--space-2);
  flex-grow: 0;
  align-self: flex-start;
  min-height: var(--table-row-h);
  display: flex;
  align-items: center;
}

// Collection badge: fix height to reference so it aligns with inputs.
// Keep flex + centering that Bulma's .tag normally provides via inline-flex.
.dataset-table-container table td > div.tag {
  height: var(--table-row-h);
  display: flex;
  align-items: center;
  justify-content: center;
}

table.main-table {
  margin-bottom: 300px;
  overflow-y: visible;
}

// ── Study column danger highlight ───────────────────────────────
.dataset-table-container table td.highlight-danger.first {
  border-top: 1px solid var(--color-danger);
}
.dataset-table-container table td.highlight-danger.last {
  border-bottom: 1px solid var(--color-danger);
}
.dataset-table-container table td.highlight-danger {
  border-left: 1px solid var(--color-danger);
  border-right: 1px solid var(--color-danger);
}

.dataset-table-container table td .name-ellipsis {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// ── Inline sample cell layout ───────────────────────────────────
// flex-start: trigger, input, ellipsis all anchor at the top of the row.
// When the input grows (error message below it), others stay at top — no
// re-centering that would misalign the trigger with the input field.
// cell-sample-inline: min-height anchors the reference line; flex-start keeps
// trigger and ellipsis at the top so they don't drift when the input grows
// (e.g. error state). Errors extend downward naturally without overflow tricks.
.cell-sample-inline {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  min-height: var(--table-row-h);
}

.sample-input-grow {
  flex: 1;
  min-width: 0;
}

// Mode-switch in inline context: fixed reference height so the trigger
// sits at the same vertical center as the adjacent input (min-height: 40px).
// border-bottom delegated to .sample-mode-trigger span only.
.cell-sample-inline :deep(.mode-switch) {
  margin-bottom: 0;
  border-bottom: none;
  min-height: var(--table-row-h);
  align-items: center;
  justify-content: center;
}

// Ellipsis in inline cell: same reference height → centered beside the input.
.cell-sample-inline .ellipsis-menu {
  min-height: var(--table-row-h);
  display: flex;
  align-items: center;
}

// Inline trigger wrapper — owns the dotted underline for precise sizing.
// align-items: flex-end keeps icon/text bottom flush with the dotted border.
// padding-inline adds equal left/right margin so the underline doesn't start
// and end hard at the icon edges.
.sample-mode-trigger {
  display: inline-flex;
  align-items: flex-end;
  gap: 0.3em;
  line-height: 1;
  flex-shrink: 0;
  border-bottom: 2px dotted var(--primary);
  padding-inline: var(--space-1);
  padding-bottom: var(--space-1);
}

// "NEW" plain-text label — slightly bigger, tight horizontal
.sample-mode-new-label {
  font-size: 0.72rem;
  font-weight: var(--font-bold);
  letter-spacing: 0.04em;
}

// DB icon — slightly larger than the mode-switch base font
.sample-mode-trigger .fa-database {
  font-size: 0.85rem;
}

// Down-arrow caret
.sample-mode-caret {
  font-size: 0.45rem;
  opacity: 0.65;
}

@keyframes blink-tag {
  50% {
    opacity: 0.15;
  }
}

.blink {
  animation-name: blink-tag;
  animation-iteration-count: 2;
  animation-timing-function: ease-in-out;
  animation-duration: 0.5s;
}
</style>
